import React from 'react';
import { Button, Form } from 'react-bootstrap';
import FormInput from 'components/FormInput';
import { ProjectDetailsProps } from './types';
import { useFormik } from 'formik';
import { SubTask, Task, Team } from 'redux/projectSlice';
import { FormDropdown } from 'components/common/FormDropdown';
import { useDispatch, useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import {
  setChecklistDetails,
  setProjectDetails,
  setSubmissionDetails,
} from 'redux/conductInspectionSlice';
import { TaskPicker } from 'components/common/TaskPicker';
import toast from 'react-hot-toast';
import { DateTimePicker } from 'components/common/DateTimePicker';
import moment, { Moment } from 'moment';
import './ProjectDetails.scss';
import { useTranslation } from 'react-i18next';
import { FormSearchDropdown } from 'components/common/FormSearchDropdown';
import { ChecklistItem } from 'pages/Checklist/types';

export const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  project,
  observation,
  schedule,
  disabled,
  noFooter,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const projectDetails = useSelector(
    (state: RootStates) => state.conductInspection.projectDetails,
  );
  const projectChecklist = useSelector(
    (state: RootStates) => state.checklist.checklists,
  );
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      projectUUID: projectDetails?._id,
      projectId: project.projectInfo.projectId,
      projectName: project.projectInfo.projectName,
      subProject: projectDetails?.subProject,
      subTask: projectDetails?.subTask,
      endTask: projectDetails?.endTask,
      selectedChecklist: projectDetails?.selectedChecklist,
      selectedDoer: projectDetails?.selectedDoer,
      selectedInspector: projectDetails?.selectedInspector,
      locations: projectDetails?.locations,
      scheduledTime: projectDetails?.scheduledTime ?? '',
    },
    onSubmit: () => {
      const values = projectDetails!;

      if (project.tasks?.length && !values?.subProject) {
        formik.setFieldError('subProject', t('error_select_sub_project'));
      } else if (values?.subProject?.subtasks?.length && !values?.subTask) {
        formik.setFieldError('subTask', t('error_select_sub_project'));
      } else if (values?.subTask?.tasks?.length && !values?.endTask) {
        formik.setFieldError('endTask', t('error_select_sub_project'));
      } else if (!observation && !values?.locations?.length) {
        formik.setFieldError('locations', t('error_select_location'));
      } else if (!observation && !values?.selectedChecklist) {
        formik.setFieldError('selectedChecklist', t('error_select_checklist'));
      } else if (schedule && !values?.selectedInspector) {
        formik.setFieldError('selectedInspector', t('error_select_inspector'));
      } else if (!schedule && !values?.selectedDoer) {
        formik.setFieldError('selectedDoer', t('error_select_doer'));
      } else if (schedule && !values?.scheduledTime) {
        formik.setFieldError('scheduledTime', t('error_select_schedule_time'));
      } else {
        onSubmit?.({
          ...values,
          id: project.projectInfo.id,
        });
      }
    },
  });

  const handleSubProjectClick = (subProject: Task) => {
    dispatch(
      setProjectDetails({
        ...projectDetails,
        subProject,
        subTask: null,
        endTask: null,
      }),
    );

    formik.setFieldValue('subProject', subProject);
    formik.setFieldValue('subTask', null);
    formik.setFieldValue('endTask', null);
  };

  const handleSubTaskClick = (subTask: SubTask) => {
    dispatch(
      setProjectDetails({
        ...projectDetails,
        subTask,
        endTask: null,
      }),
    );
    formik.setFieldValue('subTask', subTask);
    formik.setFieldValue('endTask', null);
  };

  const handleEndTaskClick = (endTask: { name: string }) => {
    dispatch(
      setProjectDetails({
        ...projectDetails,
        endTask,
      }),
    );
    formik.setFieldValue('endTask', endTask);
  };

  const handleSelectCheclistClick = (
    selectedChecklist: ChecklistItem | null,
  ) => {
    dispatch(
      setProjectDetails({
        ...projectDetails,
        selectedChecklist,
      }),
    );
    if (projectDetails?.selectedChecklist?.name !== selectedChecklist?.name) {
      dispatch(setChecklistDetails({ questions: {} }));
    }
    formik.setFieldValue('selectedChecklist', selectedChecklist);
  };

  const handleSelectDoerClick = (selectedDoer: Team) => {
    dispatch(
      setProjectDetails({
        ...projectDetails,
        selectedDoer,
      }),
    );

    if (projectDetails?.selectedDoer?.name !== selectedDoer.name) {
      dispatch(setSubmissionDetails(null));
    }
    formik.setFieldValue('selectedDoer', selectedDoer);
  };

  const handleSelectInspectorClick = (selectedInspector: Team) => {
    dispatch(
      setProjectDetails({
        ...projectDetails,
        selectedInspector,
      }),
    );

    if (projectDetails?.selectedInspector?.name !== selectedInspector.name) {
      dispatch(setSubmissionDetails(null));
    }
    formik.setFieldValue('selectedInspector', selectedInspector);
  };

  const handleAddNewLocationItem = (item: string) => {
    if (!projectDetails?.locations?.includes(item)) {
      const newLocationItems = [...(projectDetails?.locations ?? []), item];

      dispatch(
        setProjectDetails({
          ...projectDetails,
          locations: newLocationItems,
        }),
      );
      formik.setFieldValue('locations', newLocationItems);
    } else {
      toast.error(t('error_location_already_exists'));
    }
  };

  const handleRemoveLocationItem = (locations: string[]) => {
    dispatch(
      setProjectDetails({
        ...projectDetails,
        locations,
      }),
    );
    formik.setFieldValue('locations', locations);
  };

  const handleDateChange = (value: Moment) => {
    dispatch(
      setProjectDetails({
        ...projectDetails,
        scheduledTime: value.toISOString(),
      }),
    );
    formik.setFieldValue('scheduledTime', value.toISOString());
  };

  return (
    <Form
      id="workflow-project-details"
      className="project-details d-flex flex-column"
      onSubmit={formik.handleSubmit}
    >
      <div className="d-flex flex-column form-content">
        <div className="d-flex w-100 justify-content-between flex-column flex-lg-row project-details-input-container">
          <FormInput
            name="projectId"
            required
            value={formik.values.projectId}
            label={t('label_project_id')}
            disabled
          />
          <FormInput
            name="projectName"
            required
            value={formik.values.projectName}
            label={t('label_project_name')}
            disabled
          />
        </div>
        <div className="d-flex flex-column flex-lg-row dropdown-container">
          {project.tasks?.length > 0 && (
            <FormDropdown
              lable={t('label_select_sub_project')}
              items={project.tasks}
              required
              selectedItem={projectDetails?.subProject?.name}
              disabled={disabled}
              error={formik.errors.subProject}
              onSelect={handleSubProjectClick}
            />
          )}

          {projectDetails?.subProject?.subtasks?.length ? (
            <FormDropdown
              lable={t('label_select_sub_project')}
              required
              items={projectDetails?.subProject.subtasks}
              selectedItem={projectDetails?.subTask?.name}
              error={formik.errors.subTask}
              disabled={disabled}
              onSelect={handleSubTaskClick}
            />
          ) : null}

          {projectDetails?.subTask?.tasks?.length ? (
            <FormDropdown
              lable={t('label_select_sub_project')}
              required
              items={projectDetails?.subTask.tasks}
              selectedItem={projectDetails?.endTask?.name}
              error={formik.errors.endTask}
              disabled={disabled}
              onSelect={handleEndTaskClick}
            />
          ) : null}
        </div>
        {!observation && (
          <TaskPicker
            items={projectDetails?.locations ?? []}
            disabled={disabled}
            required
            additionalInfo={t('hashtag_additional_info_inspection')}
            error={formik.errors.locations}
            onAddNewItem={handleAddNewLocationItem}
            onRemoveItem={handleRemoveLocationItem}
          />
        )}
        <div className="d-flex flex-column extra-info-container">
          {!observation && (
            <FormSearchDropdown
              label={t('label_select_checklist')}
              options={projectChecklist ?? []}
              required
              selectedOption={projectDetails?.selectedChecklist ?? null}
              error={formik.errors.selectedChecklist}
              disabled={disabled}
              onSelect={handleSelectCheclistClick}
            />
          )}

          {schedule ? (
            <FormDropdown
              lable={t('label_select_inspector')}
              items={
                project.team?.filter((t) => t.role !== 'DOER' && t.name) ?? []
              }
              required
              selectedItem={projectDetails?.selectedInspector?.name}
              error={formik.errors.selectedInspector}
              disabled={disabled}
              onSelect={handleSelectInspectorClick}
            />
          ) : (
            <FormDropdown
              lable={t('label_select_doer')}
              items={
                project.team?.filter((t) => (t.role === 'DOER' || t.role === 'INSPECTOR')  && t.name) ?? []
              }
              required
              selectedItem={projectDetails?.selectedDoer?.name}
              error={formik.errors.selectedDoer}
              disabled={disabled}
              onSelect={handleSelectDoerClick}
            />
          )}
        </div>
        {schedule && (
          <div className="d-flex flex-column flex-md-row">
            <DateTimePicker
              label={t('label_schedule_date_time')}
              value={moment(formik.values.scheduledTime).toDate()}
              error={formik.errors.scheduledTime}
              onChange={handleDateChange}
            />
            <span className="empty-placeholder" />
          </div>
        )}
      </div>
      {!noFooter && (
        <div className="d-flex justify-content-end">
          <Button type="submit">
            {schedule ? t('btn_schedule') : t('btn_next')}
          </Button>
        </div>
      )}
    </Form>
  );
};
